import React, { useEffect, useRef } from "react";
import config from "appConfig";
import styles from "./turnstileChallenge.scss";

// Cloudflare Turnstile embed. The BE returns 429 + code 1093 the first time
// a client hits the reveal limit; we render this widget inline, wait for
// the user to solve it, then retry the reveal call with the token.
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileChallengeProps {
  onSuccess: (token: string) => void;
  onError?: () => void;
}

const loadTurnstileScript = () => {
  const w: any = window;
  if (w.turnstile) return Promise.resolve();

  // A concurrent instance may already be loading the script — poll instead
  // of appending it a second time.
  if (document.querySelector(`script[src^="${TURNSTILE_SRC}"]`)) {
    return new Promise((resolve) => {
      const check = () => (w.turnstile ? resolve() : setTimeout(check, 50));
      check();
    });
  }

  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.async = true;
    s.defer = true;
    s.src = TURNSTILE_SRC;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
};

const TurnstileChallenge = (props: TurnstileChallengeProps) => {
  const { onSuccess, onError } = props;
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const w: any = window;
    const cfg: any = config;

    loadTurnstileScript().then(() => {
      if (cancelled || !containerRef.current || !w.turnstile) return;
      widgetIdRef.current = w.turnstile.render(containerRef.current, {
        sitekey: cfg.turnstileSiteKey,
        callback: (token: string) => onSuccess(token),
        "error-callback": () => onError && onError(),
        "expired-callback": () => {
          if (widgetIdRef.current && w.turnstile) {
            w.turnstile.reset(widgetIdRef.current);
          }
        },
      });
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && w.turnstile) {
        try {
          w.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Widget may already be detached — ignore.
        }
      }
    };
    // Callbacks intentionally excluded to avoid re-rendering the widget on
    // each parent re-render — the callbacks live inside a closure that
    // captures the latest handler through refs would be overkill for what
    // is effectively a mount-only widget.
  }, []);

  return <div ref={containerRef} className={styles.wrapper} />;
};

export default TurnstileChallenge;



// WEBPACK FOOTER //
// ./src/components/TurnstileChallenge/index.tsx